/**
 * Single shared WebSocket for /api/notifications/ws.
 * Prevents duplicate toasts when multiple NotificationBell instances mount.
 */

import { env } from "../../config/env";
import { authStorage } from "../storage/authStorage";

export type NotificationRealtimeRow = {
  id: string;
  category: string;
  title: string;
  body: string;
  read: boolean;
  created_at: string;
  metadata?: Record<string, unknown>;
};

type RealtimeMessage =
  | { type: "notification:snapshot"; notifications: NotificationRealtimeRow[]; unread_count?: number }
  | { type: "notification:new_batch"; notifications: NotificationRealtimeRow[] }
  | { type: "notification:dm_alert"; title?: string; body?: string; thread_id?: string; message_id?: string; created_at?: string }
  | { type: "notification:read:ack"; notification_id: string }
  | { type: "notification:read_all:ack"; updated_count?: number }
  | { type: "notification:error"; message?: string }
  | { type: "notification:job_event"; notif_type?: string; title?: string; body?: string; category?: string; metadata?: Record<string, unknown> }
  /** AI media generation in-progress (every stage from TemplateProgressReporter).
   *  stage === "followup" means generation is paused waiting for user input. */
  | { type: "ai_media:progress"; stage?: string; progress?: number; message?: string; asset_type?: string; template_name?: string; session_id?: string; phase?: string; suggestions?: string[] }
  /** Deal analysis pipeline stage changes (from ExtractionAgent) */
  | { type: "deal:analysis_progress"; stage?: string; event?: string; job_id?: string; deal_id?: string; message?: string; data?: Record<string, unknown> }
  /** Internal hub event: fired when WS connects/reconnects — used for re-sync */
  | { type: "notification:connected" }
  | Record<string, unknown>;

type Listener = (msg: RealtimeMessage) => void;

const makeNotificationsWsUrl = (token: string) => {
  const base = env.apiBaseUrl.replace(/\/+$/, "");
  const wsBase = base.startsWith("https://")
    ? base.replace(/^https:\/\//, "wss://")
    : base.replace(/^http:\/\//, "ws://");
  return `${wsBase}/api/notifications/ws?token=${encodeURIComponent(token)}`;
};

const TOAST_DEDUPE_MS = 60_000;
const recentToastIds = new Map<string, number>();

export function shouldShowToastForNotification(notificationId: string): boolean {
  if (!notificationId) return true;
  const now = Date.now();
  const last = recentToastIds.get(notificationId);
  if (last != null && now - last < TOAST_DEDUPE_MS) {
    return false;
  }
  recentToastIds.set(notificationId, now);
  if (recentToastIds.size > 200) {
    for (const [id, ts] of recentToastIds) {
      if (now - ts > TOAST_DEDUPE_MS) recentToastIds.delete(id);
    }
  }
  return true;
}

const BACKOFF_DELAYS = [1500, 3000, 5000, 10000, 15000];

class NotificationRealtimeHub {
  private ws: WebSocket | null = null;
  private token: string | null = null;
  private listeners = new Set<Listener>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private disconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private refCount = 0;
  private attempt = 0;

  constructor() {
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible" && this.refCount > 0) {
          this.ensureConnected();
        }
      });
    }
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    this.refCount += 1;
    // Cancel any pending deferred disconnect since we have a new subscriber.
    if (this.disconnectTimer) {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = null;
    }
    this.ensureConnected();
    return () => {
      this.listeners.delete(listener);
      this.refCount = Math.max(0, this.refCount - 1);
      if (this.refCount === 0) {
        // Defer disconnect by one tick so React StrictMode's
        // cleanup+remount cycle doesn't close a CONNECTING socket.
        this.disconnectTimer = setTimeout(() => {
          this.disconnectTimer = null;
          if (this.refCount === 0) this.disconnect();
        }, 0);
      }
    };
  }

  /**
   * Force a reconnect with the current token from authStorage.
   * Call this after login/logout so the hub picks up the new identity immediately
   * instead of waiting for the next visibility change or component remount.
   */
  forceReconnect() {
    this.teardown();
    this.attempt = 0;
    if (this.refCount > 0) this.ensureConnected();
  }

  send(payload: Record<string, unknown>) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(payload));
  }

  private ensureConnected() {
    const token = authStorage.getToken();
    if (!token) return;
    if (this.ws && this.token === token) {
      if (
        this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING
      ) {
        return;
      }
    }
    this.teardown();
    this.token = token;
    this.connect(token);
  }

  private connect(token: string) {
    try {
      const ws = new WebSocket(makeNotificationsWsUrl(token));
      this.ws = ws;
      ws.onopen = () => {
        this.attempt = 0;
        this.broadcast({ type: "notification:connected" });
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data as string) as RealtimeMessage;
          this.broadcast(msg);
        } catch {
          /* ignore */
        }
      };
      ws.onclose = () => {
        if (this.token !== token) return;
        this.scheduleReconnect();
      };
      ws.onerror = () => {
        /* onclose handles reconnect */
      };
    } catch {
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimer || this.refCount === 0) return;
    const delay = BACKOFF_DELAYS[Math.min(this.attempt, BACKOFF_DELAYS.length - 1)];
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.refCount > 0) this.ensureConnected();
    }, delay);
  }

  // Teardown only clears ws state — does NOT clear this.token so onclose
  // handlers for the old socket can still identify they are stale.
  private teardown() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const old = this.ws;
    this.ws = null;
    this.token = null;
    if (old) {
      old.onopen = null;
      old.onmessage = null;
      old.onclose = null;
      old.onerror = null;
      try { old.close(); } catch { /* ignore */ }
    }
  }

  private disconnect() {
    this.attempt = 0;
    if (this.disconnectTimer) {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = null;
    }
    this.teardown();
  }

  private broadcast(msg: RealtimeMessage) {
    for (const listener of this.listeners) {
      try {
        listener(msg);
      } catch {
        /* ignore */
      }
    }
  }
}

export const notificationRealtime = new NotificationRealtimeHub();
